import { Link } from "react-router-dom";
import { ArrowRight, Check, Info, ShieldAlert } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import {
  ROUTES, STANDARD_LIMITS, CONDITIONS, PRIOR_APPROVAL_STEPS, RULES_SEO,
} from "./rules/extensionRulesData";

export default function ExtensionRules() {
  useSeo(RULES_SEO);
  return (
    <main data-testid="extension-rules-page">
      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="max-w-4xl">
          <div className="label-eyebrow mb-6" data-testid="rules-eyebrow">Permitted Development · Prior Approval · Planning</div>
          <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">How big can you build?</h1>
          <p className="mt-8 text-base md:text-lg text-[#6B7280] leading-relaxed">
            Most single-storey rear extensions don&apos;t need full planning permission. Many can be built under
            <b className="text-[#12263A]"> Permitted Development</b>, and you can often go deeper — up to <b className="text-[#12263A]">6m</b> (semi/terraced)
            or <b className="text-[#12263A]">8m</b> (detached) — under the <b className="text-[#12263A]">Larger Home Extension</b> scheme with a prior-approval application.
            Here&apos;s how the three routes compare.
          </p>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="px-6 md:px-12 lg:px-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {ROUTES.map((r) => (
            <div key={r.key} data-testid={`rules-card-${r.key}`} className="de-card overflow-hidden flex flex-col">
              <div className="relative aspect-[3/2] overflow-hidden bg-[#F4F4F4]">
                <img src={r.img} alt={r.title} loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 bg-[#12263A] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{r.badge}</span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-bold text-lg tracking-tight">{r.title}</h3>
                <div className="mt-2 text-[#C79A3B] font-bold text-sm">{r.depth}</div>
                <ul className="mt-4 space-y-2 text-sm text-[#6B7280] flex-1">
                  {r.points.map((p) => (
                    <li key={p} className="flex gap-2"><Check size={16} className="text-[#C79A3B] shrink-0 mt-0.5" /><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LIMITS TABLE */}
      <section className="px-6 md:px-12 lg:px-16 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="label-eyebrow mb-4">Maximum rear depth at a glance</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">Standard vs. prior-approval depths</h2>
            <div className="border border-[#E5E7EB] overflow-hidden" data-testid="rules-limits-table">
              <table className="w-full text-sm">
                <thead className="bg-[#12263A] text-white text-left">
                  <tr>{["Property", "Standard PD", "With prior approval"].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-wider">{h}</th>)}</tr>
                </thead>
                <tbody className="bg-white">
                  {STANDARD_LIMITS.map(([t, a, b]) => (
                    <tr key={t} className="border-t border-[#E5E7EB]">
                      <td className="px-4 py-3 font-medium">{t}</td>
                      <td className="px-4 py-3">{a}</td>
                      <td className="px-4 py-3 text-[#C79A3B] font-bold">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-[#6B7280]">Depth is measured from the original rear wall of the house (as it stood in 1948, or when first built).</p>
          </div>

          <div>
            <div className="label-eyebrow mb-4">The conditions that always apply</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">To stay within Permitted Development</h2>
            <ul className="space-y-3">
              {CONDITIONS.map((c) => (
                <li key={c} className="flex gap-3 text-sm"><Check size={18} className="text-[#C79A3B] shrink-0 mt-0.5" /><span>{c}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRIOR APPROVAL PROCESS */}
      <section className="bg-[#F4F4F4] border-y border-[#E5E7EB]">
        <div className="px-6 md:px-12 lg:px-16 py-16">
          <div className="label-eyebrow mb-4">The 6m / 8m route, step by step</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight max-w-3xl mb-10">How prior approval works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {PRIOR_APPROVAL_STEPS.map(([n, t, d]) => (
              <div key={n} className="de-card p-6">
                <div className="text-3xl font-black text-[#C79A3B]">{n}</div>
                <div className="mt-3 font-bold">{t}</div>
                <div className="mt-2 text-sm text-[#6B7280] leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXCLUSIONS / DISCLAIMER */}
      <section className="px-6 md:px-12 lg:px-16 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-[#E5E7EB] bg-white p-6">
            <div className="flex items-center gap-2 font-bold mb-2"><ShieldAlert size={18} className="text-[#C79A3B]" /> When PD usually doesn&apos;t apply</div>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              The larger 6m/8m allowance is <b>not</b> available on designated land — conservation areas, National Parks,
              AONBs/National Landscapes and World Heritage Sites — nor on flats, maisonettes or listed buildings, or where
              an <b>Article 4 direction</b> has removed permitted development rights. In these cases a full planning
              application is the route.
            </p>
          </div>
          <div className="border border-[#E5E7EB] bg-white p-6">
            <div className="flex items-center gap-2 font-bold mb-2"><Info size={18} className="text-[#C79A3B]" /> A friendly note</div>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              This page is a plain-English summary to help you plan — it isn&apos;t formal planning advice. Every property is
              different, so we always confirm the right route for your home before any work begins. Our instant quote runs
              a quick check for you, and you&apos;re welcome to ask Daisy (bottom-right) or our team anytime.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="bg-[#12263A] text-white p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="label-eyebrow mb-3 text-[#C79A3B]">Not sure which applies to you?</div>
            <h3 className="text-2xl md:text-4xl font-bold tracking-tight max-w-xl">Run the quick check and get a real price.</h3>
          </div>
          <Link to="/quote" data-testid="rules-cta-quote" className="de-btn-primary">Start your quote <ArrowRight size={16} /></Link>
        </div>
      </section>
    </main>
  );
}
